import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Lock } from "lucide-react";

const CountUp = ({ to, prefix = "", suffix = "", decimals = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1900;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(to * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);

  return (
    <span ref={ref} className="tabular-nums">
      {prefix}
      {val.toLocaleString(undefined, { maximumFractionDigits: decimals })}
      {suffix}
    </span>
  );
};

const targets = [
  { to: 1000, suffix: "+", label: "Nights of crisis accommodation", color: "#ff007f" },
  { to: 10000, suffix: "+", label: "Street outreach hours funded", color: "#00f0ff" },
  { to: 500, suffix: "+", label: "Case-management plans", color: "#ffb800" },
  { to: 100, suffix: "%", label: "Community-driven mission", color: "#39ff14" },
];

export const Impact = () => {
  return (
    <section id="impact" className="relative py-24 sm:py-32 px-5 sm:px-6 border-t border-white/10 overflow-hidden" data-testid="impact-section">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl mb-16">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#39ff14] mb-4">The 2027 Blueprint</div>
          <h2 className="font-display font-black uppercase tracking-tighter leading-none text-4xl sm:text-5xl lg:text-6xl">
            Targets, not <span className="text-stroke">promises.</span>
          </h2>
          <p className="font-mono text-sm text-white/50 mt-5 max-w-lg leading-relaxed">
            The full impact framework stays sealed until the white paper drops. Here&apos;s a
            glimpse of what the flock is building toward.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {targets.map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ delay: i * 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-2xl border border-white/10 bg-[#101010] p-6 sm:p-7"
              data-testid={`impact-stat-${i}`}
            >
              <div
                className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-none"
                style={{ color: t.color }}
              >
                <CountUp to={t.to} suffix={t.suffix} />
              </div>
              <div className="font-mono text-xs sm:text-sm text-white/55 mt-4 leading-relaxed">{t.label}</div>
              <div className="absolute top-6 right-6 w-2 h-2 rounded-full" style={{ background: t.color }} />
            </motion.div>
          ))}
        </div>

        <div className="mt-10 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-white/40 border border-white/10 rounded-full px-4 py-2">
          <Lock className="w-3.5 h-3.5 text-[#ffb800]" />
          Full targets unlock with the white paper — Jan 29, 2027
        </div>
      </div>

      <div className="absolute -right-24 top-1/3 w-72 h-72 rounded-full bg-[#39ff14]/10 blur-3xl pointer-events-none" />
    </section>
  );
};
